import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DomainIcon from '@mui/icons-material/Domain';
import { RoomService } from '../../api/services/roomService';
import WingFormModal from '../../components/WingFormModal';
import AddIcon from '@mui/icons-material/Add';

export default function WingSetup() {
    const [wings, setWings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchWings = async () => {
        setLoading(true);
        try {
            const response = await RoomService.getWings();
            setWings(response.data || response);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch wings:', err);
            setError(err.message || 'Failed to load wings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWings();
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Property Wings Setup</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined"
                        startIcon={<RefreshIcon />} 
                        onClick={fetchWings} 
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => setModalOpen(true)}
                    >
                        Add Wing
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <WingFormModal 
                open={modalOpen} 
                onClose={() => setModalOpen(false)} 
                onRefresh={fetchWings} 
            />

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="text.secondary">Loading wings...</Typography>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Wing Name</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {wings.length > 0 ? (
                                wings.map((wing) => (
                                    <TableRow key={wing.id} hover>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DomainIcon fontSize="small" color="secondary" />
                                                {wing.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={wing.status || 'Active'} 
                                                size="small" 
                                                color={wing.status === 'Active' ? 'success' : 'default'} 
                                                variant="outlined" 
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No wings configured for this property.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
